import { useContext, useEffect, useRef, useState } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { useRunTimeEnvironment } from '../../utils/hooks';

const SNS_CLIENT = {
  kakao: 'ce14592facabd00d83ba54056aa7117f',
  google: '592987376634-pcau3np74emhgs9n04vkcbc48u1ce318.apps.googleusercontent.com',
  apple: 'app.yaong.com',
};

/** SDK 시작 후 성공/실패 이벤트가 오지 않을 때만 웹 OAuth로 폴백 */
const NATIVE_EVENT_TIMEOUT_MS = 60_000;

const isMissingNativeHandler = (result) =>
  result === null || result === undefined || result === false || result === '';

/**
 * SNS 로그인.
 * - APP: flutter SDK 핸들러 우선 (kakao_login / google_login / apple_login)
 * - 즉시 resolve + 빈 값 → 미등록 핸들러로 보고 웹 OAuth
 * - SDK 시작됐으면 success/fail 이벤트만 대기 (짧은 noop 타이머 없음)
 * - 핸들러 reject(미지원 등) 또는 90초 이벤트 없음 → 웹 OAuth
 */
export function useSnsLogin({ onError } = {}) {
  const { API_SERVER_DOMAIN } = useContext(AuthContext);
  const RTE = useRunTimeEnvironment();
  const [loading, setLoading] = useState(false);
  const pendingRef = useRef(null);
  const eventTimeoutRef = useRef(null);
  const openWebOAuthRef = useRef(null);

  const clearEventTimeout = () => {
    if (eventTimeoutRef.current) {
      window.clearTimeout(eventTimeoutRef.current);
      eventTimeoutRef.current = null;
    }
  };

  const openWebOAuth = (snsName) => {
    clearEventTimeout();
    pendingRef.current = null;
    localStorage.setItem('lastLoginSns', snsName);
    const redirectUri = encodeURIComponent(`${API_SERVER_DOMAIN}/auth/sso/${snsName}`);

    let url = '';
    switch (snsName) {
      case 'kakao':
        url = `https://kauth.kakao.com/oauth/authorize?response_type=code&client_id=${encodeURIComponent(
          SNS_CLIENT.kakao,
        )}&redirect_uri=${redirectUri}`;
        break;
      case 'google':
        url = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${encodeURIComponent(
          SNS_CLIENT.google,
        )}&redirect_uri=${redirectUri}&response_type=code&scope=email%20profile%20openid&access_type=offline`;
        break;
      case 'apple':
        url = `https://appleid.apple.com/auth/authorize?response_type=code%20id_token&response_mode=form_post&scope=name%20email&client_id=${encodeURIComponent(
          SNS_CLIENT.apple,
        )}&redirect_uri=${redirectUri}`;
        break;
      default:
        setLoading(false);
        return;
    }

    window.location.href = url;
  };
  openWebOAuthRef.current = openWebOAuth;

  /**
   * 네이티브 핸들러 호출.
   * @returns {{ started: boolean }}
   * - 즉시 resolve + null/undefined/false/'' → 미등록 핸들러 → 웹 OAuth
   * - 그 외(아직 pending이거나 의미 있는 반환) → SDK 플로우 시작 → 이벤트만 대기
   *
   * callHandler 전체 완료를 await하지 않음 (성공 시에도 Dart가 undefined를 반환할 수 있음).
   */
  const callNativeSns = async (handlerName) => {
    const callHandler = window.flutter_inappwebview?.callHandler;
    if (typeof callHandler !== 'function') return { started: false };

    let handlerPromise;
    try {
      handlerPromise = Promise.resolve(callHandler(handlerName));
    } catch (error) {
      console.warn(`[login-v2] native ${handlerName} invoke failed`, error);
      return { started: false };
    }

    const probe = await Promise.race([
      handlerPromise.then((value) => ({ settled: true, value })),
      new Promise((resolve) => {
        window.setTimeout(() => resolve({ settled: false }), 0);
      }),
    ]);

    if (probe.settled && isMissingNativeHandler(probe.value)) {
      console.warn(`[login-v2] native ${handlerName} unregistered → web OAuth`);
      return { started: false };
    }

    handlerPromise.catch((error) => {
      if (!pendingRef.current) return;
      console.warn(`[login-v2] native ${handlerName} rejected → web OAuth`, error);
      openWebOAuthRef.current?.(pendingRef.current);
    });

    return { started: true };
  };

  /** 성공/실패 이벤트를 기다리다 없으면 웹 OAuth */
  const armNativeEventFallback = (snsName, timeoutMs = NATIVE_EVENT_TIMEOUT_MS) => {
    clearEventTimeout();
    eventTimeoutRef.current = window.setTimeout(() => {
      if (pendingRef.current !== snsName) return;
      console.warn(`[login-v2] native ${snsName} event timeout (${timeoutMs}ms) → web OAuth fallback`);
      openWebOAuthRef.current?.(snsName);
    }, timeoutMs);
  };

  const loginWithSns = async (snsName) => {
    clearEventTimeout();
    setLoading(true);
    localStorage.setItem('lastLoginSns', snsName);
    pendingRef.current = snsName;

    if (RTE === 'APP') {
      const handlerMap = {
        kakao: 'kakao_login',
        google: 'google_login',
        apple: 'apple_login',
      };
      const handler = handlerMap[snsName];
      if (handler) {
        const nativeCall = await callNativeSns(handler);
        if (nativeCall.started) {
          armNativeEventFallback(snsName, NATIVE_EVENT_TIMEOUT_MS);
          return;
        }
      }
    }

    openWebOAuth(snsName);
  };

  useEffect(() => {
    const finishPending = () => {
      clearEventTimeout();
      pendingRef.current = null;
    };

    const onKakaoSuccess = (event) => {
      finishPending();
      const userInfo = event?.detail;
      if (!userInfo?.access_token) {
        setLoading(false);
        onError?.('카카오 로그인에 실패했어요. 다시 시도해 주세요.');
        return;
      }
      window.location.href = `${API_SERVER_DOMAIN}/auth/sso/kakaoApp?code=${userInfo.access_token}`;
    };

    const onGoogleSuccess = (event) => {
      finishPending();
      const userInfo = event?.detail;
      if (!userInfo?.id_token && !userInfo?.access_token && !userInfo?.code) {
        setLoading(false);
        onError?.('구글 로그인에 실패했어요. 다시 시도해 주세요.');
        return;
      }
      // SDK: id_token → googleApp / 웹 OAuth 폴백: authorization code → google
      if (userInfo.id_token) {
        window.location.href = `${API_SERVER_DOMAIN}/auth/sso/googleApp?code=${encodeURIComponent(userInfo.id_token)}`;
        return;
      }
      const code = userInfo.code || userInfo.access_token;
      window.location.href = `${API_SERVER_DOMAIN}/auth/sso/google?code=${encodeURIComponent(code)}`;
    };

    const onAppleSuccess = (event) => {
      finishPending();
      const userInfo = event?.detail;
      if (!userInfo?.identityToken && !userInfo?.code && !userInfo?.id_token) {
        setLoading(false);
        onError?.('Apple 로그인에 실패했어요. 다시 시도해 주세요.');
        return;
      }
      // SDK: identityToken → appleApp (GET) / 웹 OAuth: Apple form_post → POST /sso/apple
      const idToken = userInfo.identityToken || userInfo.id_token;
      if (idToken) {
        window.location.href = `${API_SERVER_DOMAIN}/auth/sso/appleApp?code=${encodeURIComponent(idToken)}`;
        return;
      }
      setLoading(false);
      onError?.('Apple 로그인에 실패했어요. 다시 시도해 주세요.');
    };

    const onSnsFail = (event) => {
      finishPending();
      setLoading(false);
      const message = event?.detail?.message || '로그인에 실패했어요. 다시 시도해 주세요.';
      onError?.(message);
    };

    window.addEventListener('kakaoLoginSuccess', onKakaoSuccess);
    window.addEventListener('googleLoginSuccess', onGoogleSuccess);
    window.addEventListener('appleLoginSuccess', onAppleSuccess);
    window.addEventListener('snsLoginFail', onSnsFail);

    return () => {
      window.removeEventListener('kakaoLoginSuccess', onKakaoSuccess);
      window.removeEventListener('googleLoginSuccess', onGoogleSuccess);
      window.removeEventListener('appleLoginSuccess', onAppleSuccess);
      window.removeEventListener('snsLoginFail', onSnsFail);
      clearEventTimeout();
    };
  }, [API_SERVER_DOMAIN, onError]);

  return { loginWithSns, loading, setLoading, openWebOAuth };
}
